// AD-2761 (Maria, 23 October 2026): Undo and Redo in the Form Builder work like the buttons in Word. Each change to the
// form is one step, named in plain words, and the last 20 are kept. Nothing is stored or sent to the server.
export const UNDO_LIMIT = 20;

export const emptyHistory = {past: [], future: []};

const shown = (schema = []) => schema.filter(component => component?.type !== 'schoolLogo');
const nameOf = (component) => component?.label || component?.type || 'component';
const numbered = (schema, id) => {
    const index = shown(schema).findIndex(component => component.id === id);
    return `${index + 1}. ${nameOf(shown(schema)[index])}`;
};
const withoutId = ({id, layout, ...rest}) => JSON.stringify(rest);
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// The name of the change that turned 'before' into 'after', e.g. "Added Single Line" or "Deleted 3. Notes".
export const describeChange = (before = [], after = []) => {
    const was = shown(before);
    const now = shown(after);
    const wasIds = new Set(was.map(component => component.id));
    const nowIds = new Set(now.map(component => component.id));

    const added = now.filter(component => !wasIds.has(component.id));
    if (added.length) {
        const [component] = added;
        const copied = was.some(other => withoutId(other) === withoutId(component));
        return copied ? `Copied ${numbered(after, component.id)}` : `Added ${nameOf(component)}`;
    }

    const removed = was.filter(component => !nowIds.has(component.id));
    if (removed.length) return `Deleted ${numbered(before, removed[0].id)}`;

    if (now.some((component, index) => was[index]?.id !== component.id)) {
        // The component that changed place the most is the one that was moved.
        const moved = now.reduce((best, component, index) => {
            const from = was.findIndex(other => other.id === component.id);
            const distance = Math.abs(from - index);
            return distance > best.distance ? {component, distance, up: index < from} : best;
        }, {component: null, distance: 0, up: false});
        return `Moved ${numbered(after, moved.component.id)} ${moved.up ? 'up' : 'down'}`;
    }

    const changed = now.find((component, index) => !same(component, was[index]));
    if (!changed) return 'Changed the form';
    const old = was.find(component => component.id === changed.id);
    if (!same({...changed, layout: undefined}, {...old, layout: undefined})) return `Edited ${numbered(after, changed.id)}`;
    return `Changed the row of ${numbered(after, changed.id)}`;
};

// A change was made: the form before it can be gone back to, and anything that could be redone is cleared.
export const recordChange = (history, before, after) => {
    if (same(before, after)) return history;
    const past = [...history.past, {schema: before, label: describeChange(before, after)}];
    return {past: past.slice(-UNDO_LIMIT), future: []};
};

// Undo the last 'count' changes. Returns the form to show and the new history, or null when there is nothing to undo.
export const undo = (history, current, count = 1) => {
    if (!history.past.length || count < 1) return null;
    const steps = Math.min(count, history.past.length);
    const past = history.past.slice(0, history.past.length - steps);
    const undone = history.past.slice(history.past.length - steps);
    // Each undone step can be redone: the form after it is the form before the next one, or the current form.
    const redoSteps = undone.map((entry, index) => ({
        schema: index + 1 < undone.length ? undone[index + 1].schema : current,
        label: entry.label,
    }));
    return {schema: undone[0].schema, history: {past, future: [...redoSteps, ...history.future]}};
};

export const redo = (history, current) => {
    if (!history.future.length) return null;
    const [next, ...future] = history.future;
    return {
        schema: next.schema,
        history: {past: [...history.past, {schema: current, label: next.label}].slice(-UNDO_LIMIT), future},
    };
};
